import type { ApolloCache, FetchResult } from "@apollo/client";
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
  useSubscription,
  useFragment,
} from "@apollo/client/react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useOverlayScrollbars } from "overlayscrollbars-react";
import {
  ThreadFragment,
  type ThreadTabStatusFragment,
} from "@/__generated__/graphql";
import { Mutations, Queries, Subscriptions } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useScope } from "@/hooks/useScope";
import { idToTypename } from "@/objects/@utils";
import { State } from "@/state";
import { formatApolloError } from "@/utils/graphqlErrors";
import {
  NEW_THREAD_THREAD_ID,
  ThreadIdType,
  type ThreadMessageContextItemType,
} from "@/state/@types";
import { usePendingChanges } from "@/components/organisms/PendingSession/PendingSession.hooks";
import {
  type StreamingContent,
  messageIsWorkflowStep,
} from "@/components/organisms/ToolCallCard/ToolCallCard.tools";
import type {
  MessageItem,
  ThreadMessagesConnection,
  ThreadWithMessages,
} from "./Thread.types";
import type { PendingContextReference } from "./ThreadMessageList";
import { threadNodeIdToTabId, tabIdToThreadNodeId } from "@/state/threadTabs";
import {
  deriveAgentTurn,
  latestAgentReply,
  mergeThreadMessageEdges,
  mergeUpdateNodeMessagesIntoCache,
  MESSAGES_PER_PAGE,
  setThreadPendingSession,
  pendingDeltaCountFromSession,
  seedThreadMessagesQueryCache,
  writePersistedMessage,
  writeStreamingContent,
  THREAD_TAB_STATUS_FRAGMENT,
  type AgentTurnPhase,
} from "./Thread.utils";

type PromptArray = Array<{ text?: string; id?: string }>;

/** What went wrong with the latest turn, and what the user can do about it. */
type TurnNotice = {
  text: string;
  action?: { label: string; onClick: () => void };
  onDismiss?: () => void;
};

/** One frame of the ``streamThread`` subscription. */
type ThreadStreamEvent = {
  messageIdentifier?: string | null;
  content?: StreamingContent | null;
  error?: unknown;
  stopped?: boolean;
  done?: boolean;
  message?: MessageItem | null;
};

/**
 * Query, stream, send, and messages for the thread panel.
 *
 * The Apollo cache is the only source of truth. Streamed rows are written
 * under the message and content identifiers the server names, so they land in
 * the same normalized entries the query reads and the finished turn merges
 * into them field-by-field — there is no second list to reconcile against and
 * nothing to arbitrate when the stream ends.
 */
export function useThreadPanel(
  activeThreadNodeId: string | null,
  {
    timing = TURN_TIMING,
  }: {
    /** How long a turn may go quiet before it is re-read, and before it is stalled. */
    timing?: typeof TURN_TIMING;
  } = {},
) {
  const apolloClient = useApolloClient();
  const { userEntity, activeEntityIdentifiers, scopedActiveEntityIdentifier } =
    useScope();
  const setActiveThreadNodeId = State((s) => s.setActiveThreadNodeId);
  const openThreadTab = State((s) => s.openThreadTab);
  const openNewThreadTab = State((s) => s.openNewThreadTab);
  const promoteNewThreadTab = State((s) => s.promoteNewThreadTab);
  const setThreadTabLabel = State((s) => s.setThreadTabLabel);
  const setThreadTabActivity = State((s) => s.setThreadTabActivity);
  const setSnackbarMessage = State((s) => s.setSnackbarMessage);
  const account = userEntity?.accounts?.[0] ?? null;
  // The business a new chat is made in - one the person picks before its first
  // message - or, with none picked, their own, private. A chat's business is
  // its for good, so the pick is let go of as another chat is opened.
  const [newThreadEntityIdentifier, setNewThreadEntityIdentifier] = useState<
    string | null
  >(null);
  const [pickedFor, setPickedFor] = useState(activeThreadNodeId);
  if (pickedFor !== activeThreadNodeId) {
    setPickedFor(activeThreadNodeId);
    setNewThreadEntityIdentifier(null);
  }

  const rootRef = useRef<HTMLDivElement>(null);
  const scrollHostRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // The message list keeps ``rootRef`` as the element that scrolls — it is the
  // IntersectionObserver root for paging and the anchor ancestor the pinned
  // prompts stick to — so the scrollbar is drawn into a host around it rather
  // than into a viewport OverlayScrollbars would generate inside it.
  const [initializeScrollbars] = useOverlayScrollbars({
    defer: true,
    options: { scrollbars: {} },
  });

  useEffect(() => {
    const host = scrollHostRef.current;
    const viewport = rootRef.current;
    if (!host || !viewport) return;
    initializeScrollbars({ target: host, elements: { viewport } });
  }, [initializeScrollbars]);

  /** The prompt of a send the server never received, kept for the user to retry. */
  const [failedSend, setFailedSend] = useState<PromptArray | null>(null);
  /** The live stream gave up; the turn keeps converging over HTTP meanwhile. */
  const [streamDown, setStreamDown] = useState(false);
  /** A new thread's first turn, held until the entity it is created in is known. */
  const [queuedNewThread, setQueuedNewThread] = useState<{
    prompt: PromptArray;
    context: string[];
    draftMessage?: { identifier: string };
  } | null>(null);
  const lastSentPromptRef = useRef<PromptArray | null>(null);
  const [streamThreadIdentifierOverride, setStreamThreadIdentifierOverride] =
    useState<string | null>(null);
  // Closed until asked for: opened on load, the tray pushed the thread about
  // as it filled in.
  const [pendingChangesExpanded, setPendingChangesExpanded] = useState(false);
  // Asked for from elsewhere - a draft's Review in triage - the thread opens
  // with its changes laid out open, to put right before they are confirmed:
  // for that thread, until another is opened.
  const threadReviewRequestNodeId = State((s) => s.threadReviewRequestNodeId);
  const [isReviewingChanges, setIsReviewingChanges] = useState(false);
  useEffect(() => {
    setIsReviewingChanges(false);
  }, [activeThreadNodeId]);
  useEffect(() => {
    if (
      threadReviewRequestNodeId == null ||
      threadReviewRequestNodeId !== activeThreadNodeId
    ) {
      return;
    }
    setPendingChangesExpanded(true);
    setIsReviewingChanges(true);
    State.setState({ threadReviewRequestNodeId: null });
  }, [activeThreadNodeId, threadReviewRequestNodeId]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [confirmingSession, setConfirmingSession] = useState(false);
  const [rejectingSession, setRejectingSession] = useState(false);
  const oldestCursorRef = useRef<string | null>(null);
  /**
   * The turn the user just sent, shown beneath the list until the server's own
   * copy of it arrives.
   *
   * It is deliberately not written into the cache: a client-authored row would
   * need a made-up id and timestamp, and would then have to be reconciled back
   * out of a list it had already sorted itself into.
   */
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  /** The records that turn was sent about, shown with it until it is echoed. */
  const [pendingContext, setPendingContext] = useState<
    PendingContextReference[]
  >([]);
  /** The names of the files that turn carried, shown with it. */
  const [pendingFiles, setPendingFiles] = useState<string[]>([]);
  const userTurnsAtSendRef = useRef(0);

  const isExistingThread =
    !!activeThreadNodeId && activeThreadNodeId !== NEW_THREAD_THREAD_ID;

  const threadQueryVariables = useMemo(
    () =>
      isExistingThread && activeThreadNodeId
        ? { id: activeThreadNodeId, last: MESSAGES_PER_PAGE }
        : undefined,
    [isExistingThread, activeThreadNodeId],
  );

  // A view of the cache: one read when a thread opens, then never the network
  // on its own. Every later read is an explicit ``refetchThread``, so a
  // streamed write that leaves a row briefly incomplete cannot send the list
  // to the network, and no fetch policy can turn a re-read into a throw.
  const {
    data,
    loading: isLoading,
    error: threadQueryError,
  } = useQuery(Queries.THREAD_WITH_MESSAGES_QUERY, {
    variables: threadQueryVariables!,
    skip:
      !threadQueryVariables ||
      idToTypename(activeThreadNodeId!) !== "ThreadObject",
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-only",
    notifyOnNetworkStatusChange: false,
    returnPartialData: true,
  });

  // ``returnPartialData`` types the node as deep-partial; the panel treats a
  // ThreadObject hit for the active id as the full shape (it always was).
  const rawThreadNode = data?.node;
  const threadFragment =
    rawThreadNode?.__typename === "ThreadObject" &&
    rawThreadNode.id === activeThreadNodeId
      ? (rawThreadNode as ThreadWithMessages)
      : undefined;

  const messagesForList = threadFragment?.messages;
  const messageEdges = useMemo(
    () => messagesForList?.edges ?? [],
    [messagesForList?.edges],
  );
  const userTurns = useMemo(
    () => messageEdges.filter((edge) => edge.node.account != null).length,
    [messageEdges],
  );

  // The server's copy of the turn has landed, so stop showing our own -
  // decided as the list is drawn, not in an effect after it. An effect runs
  // once the render holding the server's copy has already been painted, so
  // the message stood twice for a frame and the list, anchored to its bottom,
  // jumped as the second one went. The effect only lets go of the text.
  const isEchoed =
    pendingPrompt !== null && userTurns > userTurnsAtSendRef.current;
  const shownPendingPrompt = isEchoed ? null : pendingPrompt;
  useEffect(() => {
    if (isEchoed) setPendingPrompt(null);
  }, [isEchoed]);

  useEffect(() => {
    const pageInfo = (
      threadFragment?.messages as
        | {
            pageInfo?: {
              hasPreviousPage?: boolean;
              startCursor?: string | null;
            };
          }
        | undefined
    )?.pageInfo;
    if (!pageInfo) return;
    setHasMoreMessages(pageInfo.hasPreviousPage ?? false);
    oldestCursorRef.current = pageInfo.startCursor ?? null;
  }, [threadFragment?.messages]);

  const streamThreadIdentifier =
    threadFragment?.identifier ?? streamThreadIdentifierOverride ?? null;

  /**
   * The latest frame of each row the stream has written, numbered in arrival
   * order, so a read of the thread can tell which frames landed while it was
   * out.
   */
  const streamedRowsRef = useRef(
    new Map<
      string,
      { messageIdentifier: string; content: StreamingContent; seq: number }
    >(),
  );
  const frameSeqRef = useRef(0);
  /**
   * A stop the user asked for, for the stream handler and the thread read,
   * which do not re-read state. See ``stopRequested``.
   */
  const stopRequestedRef = useRef(false);

  /**
   * Re-read the whole turn list from the server, keeping any extra pages loaded.
   *
   * A one-off network read written into the cache the panel watches. It
   * rejects when the server cannot be reached, which callers report.
   *
   * The read is the server's copy as it was when taken, and a streaming row is
   * saved blank until the run finishes it. So any frame that arrived while the
   * read was out is written again after it: in prod a read sent on the first
   * frame can come back after the stream has finished the reply, and written
   * over it the reply vanished until ``done``.
   */
  const refetchThread = useCallback(async () => {
    if (!threadQueryVariables) return;
    const sentAfterFrame = frameSeqRef.current;
    const result = await apolloClient.query({
      query: Queries.THREAD_WITH_MESSAGES_QUERY,
      variables: {
        ...threadQueryVariables,
        last: Math.max(messageEdges.length, MESSAGES_PER_PAGE),
      },
      fetchPolicy: "network-only",
    });
    // A stopped turn takes no more frames, so the read is its record.
    if (stopRequestedRef.current) return result;
    for (const frame of streamedRowsRef.current.values()) {
      if (frame.seq > sentAfterFrame) {
        writeStreamingContent(
          apolloClient.cache,
          frame.messageIdentifier,
          frame.content,
        );
      }
    }
    return result;
  }, [apolloClient, threadQueryVariables, messageEdges.length]);

  const [fetchMore, { loading: loadingMore }] = useLazyQuery(
    Queries.THREAD_LOAD_MORE_MESSAGES_QUERY,
    { fetchPolicy: "network-only" },
  );

  const loadMoreMessages = useCallback(async () => {
    if (
      !activeThreadNodeId ||
      !oldestCursorRef.current ||
      !hasMoreMessages ||
      loadingMore
    ) {
      return;
    }
    const result = await fetchMore({
      variables: {
        id: activeThreadNodeId,
        last: MESSAGES_PER_PAGE,
        before: oldestCursorRef.current,
      },
    });
    const conn = (
      result.data as {
        node?: {
          messages?: {
            edges?: Array<{ node: MessageItem }>;
            pageInfo?: {
              hasPreviousPage?: boolean;
              startCursor?: string | null;
            };
          };
        };
      }
    )?.node?.messages;
    const older = conn?.edges ?? [];
    if (!older.length) return;

    const threadKey = apolloClient.cache.identify({
      __typename: "ThreadObject",
      id: activeThreadNodeId,
    });
    if (threadKey) {
      const olderConn = {
        __typename: "MessageObjectORMConnection" as const,
        edges: older.map((edge, index) => ({
          __typename: "MessageObjectEdge" as const,
          cursor: `cursor-${edge.node.identifier ?? index}`,
          node: { ...edge.node, __typename: "MessageObject" as const },
        })),
        pageInfo: conn?.pageInfo,
      };
      apolloClient.cache.modify({
        id: threadKey,
        fields: {
          messages(existing, { readField }) {
            return mergeThreadMessageEdges(existing, olderConn, readField);
          },
        },
      });
    }
  }, [
    activeThreadNodeId,
    hasMoreMessages,
    loadingMore,
    fetchMore,
    apolloClient.cache,
  ]);

  // A thread opens at its latest turn. The bottom anchor keeps the view there
  // as a reply grows, but only once it is there: nothing put it there, so a
  // thread opened at its top, with its oldest messages in view and the
  // sentinel for loading older ones already on screen. Once per thread, as
  // soon as its messages are drawn — after that the reader's scroll is theirs.
  const openedAtLatestRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const viewport = rootRef.current;
    const thread = activeThreadNodeId ?? NEW_THREAD_THREAD_ID;
    if (!viewport || messageEdges.length === 0) return;
    if (openedAtLatestRef.current === thread) return;
    openedAtLatestRef.current = thread;
    viewport.scrollTop = viewport.scrollHeight;
  }, [activeThreadNodeId, messageEdges.length]);

  // What rises out of the composer - the page's tray, the pending changes -
  // takes its height from the view, and the bottom anchor does not follow a
  // view that shrinks: it holds the scroll offset, so the latest turn slid
  // under the composer. A reader at the latest turn stays there as the view
  // changes size; one who has scrolled up is left where they are.
  useEffect(() => {
    const viewport = rootRef.current;
    if (!viewport) return;
    let isAtLatest = true;
    // Where this put the view last. The scroll that follows is its own, and
    // by the time it is heard the view may have shrunk again - measured
    // then, it read as the reader having scrolled up.
    let placedAt: number | null = null;
    const onScroll = () => {
      if (placedAt !== null && Math.abs(viewport.scrollTop - placedAt) < 1) {
        return;
      }
      placedAt = null;
      isAtLatest =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 2;
    };
    const observer = new ResizeObserver(() => {
      if (!isAtLatest) return;
      viewport.scrollTop = viewport.scrollHeight;
      placedAt = viewport.scrollTop;
    });
    viewport.addEventListener("scroll", onScroll, { passive: true });
    observer.observe(viewport);
    return () => {
      viewport.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  const onLoadMoreRef = useRef(loadMoreMessages);
  onLoadMoreRef.current = loadMoreMessages;

  useEffect(() => {
    if (!hasMoreMessages) return;
    const loadMoreEl = loadMoreRef.current;
    const scrollRoot = rootRef.current;
    if (!loadMoreEl || !scrollRoot) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMoreRef.current();
      },
      { root: scrollRoot, rootMargin: "300px", threshold: 0 },
    );
    observer.observe(loadMoreEl);
    return () => observer.disconnect();
  }, [hasMoreMessages]);

  const isWorkflowExecutionThread = useMemo(() => {
    if (threadFragment?.source === "rule") return true;
    return messageEdges.some((e) => messageIsWorkflowStep(e.node));
  }, [threadFragment?.source, messageEdges]);

  const isInitialThreadLoading =
    isExistingThread &&
    !threadFragment &&
    isLoading &&
    !threadQueryError &&
    shownPendingPrompt === null;

  /**
   * The message being written, as the server keeps it while files are dropped
   * on it: named here, so every file can upload at once without waiting on
   * the first to create it.
   */
  const draftMessageIdentifierRef = useRef<string>(crypto.randomUUID());
  /**
   * Files dropped on the thread, in the order dropped. One without a
   * ``messageFileId`` is still uploading, and the message waits for it.
   */
  const [draftFiles, setDraftFiles] = useState<
    Array<{ key: string; name: string; messageFileId?: string }>
  >([]);
  /** The draft the last send took with it, for a send that fails to put back. */
  const lastSentDraftRef = useRef<{
    identifier: string;
    files: typeof draftFiles;
  } | null>(null);

  // The send never reached the server: keep the user's turn on screen with a
  // way to send it again rather than letting it vanish. The files it carried
  // go back on the composer, so sending it again sends them too.
  const handleThreadMutationError = useCallback(() => {
    setFailedSend(lastSentPromptRef.current);
    const sentDraft = lastSentDraftRef.current;
    lastSentDraftRef.current = null;
    // Files dropped since are on a draft of their own, which is left as it is.
    if (sentDraft && draftFiles.length === 0) {
      draftMessageIdentifierRef.current = sentDraft.identifier;
      setDraftFiles(sentDraft.files);
    }
  }, [draftFiles.length]);

  const { mutate: createThread, loading: creatingThreadLoading } =
    useBackendMutation({
      mutation: Mutations.CREATE_NODE,
      callbacks: {
        onSuccess: (created: ThreadFragment[]) => {
          const thread = created?.[0];
          if (thread?.__typename !== "ThreadObject" || !thread.id) return;
          // ``createNode`` returns the thread only; its messages arrive with
          // the stream and the refetch that follows it.
          seedThreadMessagesQueryCache(apolloClient.cache, thread);
          if (thread.identifier) {
            setStreamThreadIdentifierOverride(thread.identifier);
          }
          promoteNewThreadTab(thread.id, thread.name ?? undefined);
          void apolloClient.query({
            query: Queries.THREAD_WITH_MESSAGES_QUERY,
            variables: { id: thread.id, last: MESSAGES_PER_PAGE },
            fetchPolicy: "network-only",
          });
        },
        onError: handleThreadMutationError,
      },
      // Not ThreadWithMessages — the hook may be skipped with no $id, which
      // triggers "Variable '$id' must not be null".
      refetchQueries: ["ThreadsQuery"],
    });

  const { mutate: updateThread, loading: updatingThreadLoading } =
    useBackendMutation({
      mutation: Mutations.UPDATE_THREAD,
      callbacks: { onError: handleThreadMutationError },
    });

  const [stopThread, { loading: stoppingThread }] = useMutation(
    Mutations.STOP_THREAD,
  );
  const { mutate: uploadMessageFile } = useBackendMutation({
    mutation: Mutations.UPLOAD_MESSAGE_FILE,
  });
  const { mutate: deleteNodes } = useBackendMutation({
    mutation: Mutations.DELETE_NODES,
  });
  const { mutate: createRuleMutation } = useBackendMutation({
    mutation: Mutations.CREATE_NODE,
    refetchQueries: ["RulesConnectionQuery", "NodeQuery"],
  });
  // The tray decides a whole session: every card re-reads its change after, as
  // it does when deciding one (see ``ToolCallCardPendingStatus``).
  const [confirmPendingNodes] = useMutation(Mutations.CONFIRM_PENDING_NODES, {
    refetchQueries: ["PendingChangeQuery"],
  });
  const [rejectPendingNodes] = useMutation(Mutations.REJECT_PENDING_NODES, {
    refetchQueries: ["PendingChangeQuery"],
  });

  // Confirm and reject are the same round-trip against different mutations,
  // which the generated types keep as separate shapes. The tray goes as it is
  // clicked and the server works through the session behind it: a statement's
  // fifteen hundred lines took it a minute, and the tray waited all of it.
  // Refused, the tray comes back as the server still holds it.
  const resolvePendingSession = useCallback(
    async (
      pendingSessionId: string | null,
      run: (sessionIdentifier: string) => Promise<unknown>,
      setBusy: (busy: boolean) => void,
    ) => {
      if (!pendingSessionId || !activeThreadNodeId) return;
      setBusy(true);
      setThreadPendingSession(apolloClient.cache, activeThreadNodeId, null);
      try {
        await run(pendingSessionId);
        await refetchThread();
      } catch (error) {
        setSnackbarMessage(formatApolloError(error));
        void refetchThread();
      } finally {
        setBusy(false);
      }
    },
    [apolloClient.cache, activeThreadNodeId, refetchThread, setSnackbarMessage],
  );

  const handleConfirmPending = useCallback(
    (pendingSessionId: string | null) =>
      resolvePendingSession(
        pendingSessionId,
        (sessionIdentifier) =>
          confirmPendingNodes({ variables: { input: { sessionIdentifier } } }),
        setConfirmingSession,
      ),
    [resolvePendingSession, confirmPendingNodes],
  );

  const handleRejectPending = useCallback(
    (pendingSessionId: string | null) =>
      resolvePendingSession(
        pendingSessionId,
        (sessionIdentifier) =>
          rejectPendingNodes({ variables: { input: { sessionIdentifier } } }),
        setRejectingSession,
      ),
    [resolvePendingSession, rejectPendingNodes],
  );

  const handleDropFiles = useCallback(
    (files: File[]) => {
      if (!scopedActiveEntityIdentifier) {
        setSnackbarMessage("Pick an entity to attach files to.");
        return;
      }
      for (const file of files) {
        const key = crypto.randomUUID();
        setDraftFiles((current) => [...current, { key, name: file.name }]);
        uploadMessageFile({
          variables: {
            input: {
              entityIdentifier: scopedActiveEntityIdentifier,
              draftMessageIdentifier: draftMessageIdentifierRef.current,
              file,
            },
          },
          callbacks: {
            onSuccess: (messageFile: { id: string }) =>
              setDraftFiles((current) =>
                current.map((draftFile) =>
                  draftFile.key === key
                    ? { ...draftFile, messageFileId: messageFile.id }
                    : draftFile,
                ),
              ),
            onError: () =>
              setDraftFiles((current) =>
                current.filter((draftFile) => draftFile.key !== key),
              ),
          },
        });
      }
    },
    [scopedActiveEntityIdentifier, uploadMessageFile, setSnackbarMessage],
  );

  const handleRemoveDraftFile = useCallback(
    (key: string) => {
      const removed = draftFiles.find((draftFile) => draftFile.key === key);
      if (!removed?.messageFileId) return;
      setDraftFiles((current) =>
        current.filter((draftFile) => draftFile.key !== key),
      );
      deleteNodes({ variables: { input: { ids: [removed.messageFileId] } } });
    },
    [draftFiles, deleteNodes],
  );

  /**
   * Sends the composer's prompt, answering whether it went: ``false`` leaves
   * what was typed in the composer.
   */
  const handleSendMessage = useCallback(
    (typedPrompt: Array<{ text?: string; id?: string }>): boolean => {
      if (draftFiles.some((draftFile) => !draftFile.messageFileId)) {
        setSnackbarMessage("Your files are still uploading.");
        return false;
      }
      // Dropped files say enough on their own.
      const promptArray = typedPrompt.some(
        (item) => item.text?.trim() || item.id,
      )
        ? typedPrompt
        : draftFiles.length > 0
          ? [{ text: "Process these files" }]
          : [];
      if (promptArray.length === 0) return false;
      const draftMessage =
        draftFiles.length > 0
          ? { identifier: draftMessageIdentifierRef.current }
          : undefined;
      // Sending again is the user picking the thread back up after a stop.
      setStopRequested(false);
      stopRequestedRef.current = false;

      // What the user added, and what the page offers unless it is off.
      const { isPageContextEnabled } = State.getState();
      const messageContext = State.getState().activeMessageContext.filter(
        (item) => !item.isPage || isPageContextEnabled,
      );
      lastSentPromptRef.current = promptArray;
      setFailedSend(null);

      const promptText = promptArray
        .map((item) => item.text?.trim())
        .filter(Boolean)
        .join(" ");
      const normalizedPrompt = promptText.toLowerCase();

      if (
        normalizedPrompt.startsWith("workflow:") ||
        normalizedPrompt.startsWith("rule:")
      ) {
        const workflowCtx = messageContext.find(
          (c) => c.typename === "RuleDefinitionObject",
        );
        const createNodePayload = (type: string, name: string) => {
          if (!workflowCtx) return;
          createRuleMutation({
            variables: {
              input: {
                entityIdentifier: userEntity?.identifier,
                node: {
                  ruleNode: {
                    rule: { identifier: workflowCtx.identifier },
                    type,
                    name,
                  },
                },
              },
            },
          });
        };
        if (
          (!workflowCtx && normalizedPrompt.includes("create workflow")) ||
          normalizedPrompt.includes("create rule")
        ) {
          createRuleMutation({
            variables: {
              input: {
                entityIdentifier: userEntity?.identifier,
                node: { rule: { name: "Rule from thread" } },
              },
            },
          });
        } else if (normalizedPrompt.includes("add if")) {
          createNodePayload("IF_BRANCH", "If");
        } else if (normalizedPrompt.includes("add else if")) {
          createNodePayload("ELSE_IF_BRANCH", "Else If");
        } else if (normalizedPrompt.includes("add else")) {
          createNodePayload("ELSE_BRANCH", "Else");
        } else if (normalizedPrompt.includes("add action")) {
          createNodePayload("ACTION", "Action");
        }
      }

      userTurnsAtSendRef.current = userTurns;
      setPendingContext(
        messageContext.map(({ id, identifier, name, typename }) => ({
          id,
          identifier,
          name,
          typename,
        })),
      );
      setPendingFiles(draftFiles.map((draftFile) => draftFile.name));
      setPendingPrompt(promptText);

      if (activeThreadNodeId == null) {
        // Sent by the effect below once the entity is known.
        setQueuedNewThread({
          prompt: promptArray,
          context: messageContext.map((c) => c.id),
          draftMessage,
        });
      } else if (threadFragment) {
        if (threadFragment.identifier) {
          setStreamThreadIdentifierOverride(threadFragment.identifier);
        }
        const fromMessageIdentifier = messageEdges.map((e) => e.node)[
          State.getState().activeMessageIndex
        ]?.identifier;

        updateThread({
          variables: {
            input: {
              id: activeThreadNodeId,
              entityIdentifiers: activeEntityIdentifiers,
              node: {
                thread: {
                  prompt: promptArray,
                  fromMessage: fromMessageIdentifier
                    ? { identifier: fromMessageIdentifier }
                    : undefined,
                  context: messageContext.map((c) => c.id),
                  draftMessage,
                },
              },
            },
          },
          update: (cache: ApolloCache, result: FetchResult) => {
            const node = (
              result.data as {
                updateNode?: {
                  data?: {
                    __typename?: string;
                    id?: string;
                    messages?: unknown;
                  };
                };
              }
            )?.updateNode?.data;
            if (
              node?.__typename !== "ThreadObject" ||
              node.id !== activeThreadNodeId
            ) {
              return;
            }
            const incoming = node.messages;
            if (
              incoming &&
              typeof incoming === "object" &&
              "edges" in incoming &&
              Array.isArray((incoming as { edges: unknown[] }).edges)
            ) {
              mergeUpdateNodeMessagesIntoCache(
                cache,
                activeThreadNodeId,
                incoming as Parameters<
                  typeof mergeUpdateNodeMessagesIntoCache
                >[2],
              );
            }
          },
        });
      }

      // What the user added went with the message; what a page offers
      // (``isPage``) is the page's, and stays.
      const {
        activeMessageContext,
        setActiveMessageContext,
        setActiveMessageIndex,
      } = State.getState();
      setActiveMessageContext(
        activeMessageContext.filter((item) => item.isPage),
      );
      setActiveMessageIndex(0);
      lastSentDraftRef.current = draftMessage
        ? { identifier: draftMessage.identifier, files: draftFiles }
        : null;
      if (draftMessage) {
        // Those files went with the message; the next ones start another.
        draftMessageIdentifierRef.current = crypto.randomUUID();
        setDraftFiles([]);
      }
      return true;
    },
    [
      draftFiles,
      setSnackbarMessage,
      activeThreadNodeId,
      threadFragment,
      messageEdges,
      updateThread,
      userEntity,
      activeEntityIdentifiers,
      createRuleMutation,
      userTurns,
    ],
  );

  // A new thread is created in the business picked for it, else the user's own
  // entity, which a freshly loaded page may still be fetching. A turn sent
  // before then waits here rather than going out without one - which the
  // server rejects, losing the message.
  const entityReady =
    !!userEntity?.identifier && activeEntityIdentifiers.length > 0;
  useEffect(() => {
    if (!queuedNewThread || !entityReady) return;
    setQueuedNewThread(null);
    createThread({
      variables: {
        input: {
          entityIdentifier: newThreadEntityIdentifier ?? userEntity!.identifier,
          entityIdentifiers: activeEntityIdentifiers,
          node: {
            thread: {
              prompt: queuedNewThread.prompt,
              context: queuedNewThread.context,
              draftMessage: queuedNewThread.draftMessage,
            },
          },
        },
      },
    });
  }, [
    queuedNewThread,
    entityReady,
    userEntity,
    activeEntityIdentifiers,
    createThread,
    newThreadEntityIdentifier,
  ]);

  const { pendingNodes, pendingSessionId } = usePendingChanges(
    threadFragment?.pendingSession,
  );

  useEffect(() => {
    if (!threadQueryError) return;
    setSnackbarMessage(formatApolloError(threadQueryError));
  }, [threadQueryError, setSnackbarMessage]);

  // ── The latest turn ───────────────────────────────────────────────────
  //
  // Where the reply stands is read from what the server saved - the run's
  // status on the agent message - and a turn in progress keeps re-reading the
  // thread until that says it is over. The stream only makes the reply
  // arrive sooner; nothing about the turn depends on which of its frames
  // got through.
  const sending =
    creatingThreadLoading || updatingThreadLoading || queuedNewThread !== null;
  const messageNodes = useMemo(
    () => messageEdges.map((edge) => edge.node),
    [messageEdges],
  );
  const agentReply = latestAgentReply(messageNodes);

  /**
   * A stop the user asked for and the run has not finished obeying.
   *
   * Held for the thread rather than against a particular reply: the stop can
   * land before the server's copy of the turn exists, and the run goes on
   * writing rows for seconds afterwards. The next message the user sends
   * clears it.
   */
  const [stopRequested, setStopRequested] = useState(false);

  const handleStopThread = useCallback(() => {
    if (!threadFragment?.identifier) return;
    // The run cannot stop mid-tool, so it keeps going for a moment after this.
    // The turn stops looking live at the click rather than when the server
    // catches up: its rows end, and the composer takes its send button back.
    setStopRequested(true);
    stopRequestedRef.current = true;
    stopThread({
      variables: { input: { threadIdentifier: threadFragment.identifier } },
    });
  }, [threadFragment?.identifier, stopThread]);

  // Anything that says the turn has moved on: a new message, the run's
  // status or heartbeat, a new row, more text.
  const turnProgressKey = [
    messageNodes.length,
    agentReply?.execution?.status,
    agentReply?.execution?.updatedAt,
    (agentReply?.content ?? [])
      .map((row) => `${row.identifier}:${row.status}:${row.text?.length ?? 0}`)
      .join(","),
  ].join("|");
  const serverPhase = deriveAgentTurn(messageNodes, {
    sending,
    sendFailed: failedSend !== null,
    awaitingEcho: shownPendingPrompt !== null,
  });
  const reconciler = useAgentTurnReconciler({
    // A stopped turn is not watched for progress: its refetches would keep
    // pulling back the rows the run is still writing as it winds down.
    inProgress: serverPhase === "working" && !stopRequested,
    progressKey: turnProgressKey,
    refetch: refetchThread,
    timing,
  });
  const turnPhase: AgentTurnPhase = stopRequested
    ? "idle"
    : serverPhase === "working" && reconciler.stalled
      ? "stalled"
      : serverPhase;
  const awaitingAgentReply = turnPhase === "sending" || turnPhase === "working";
  // A turn that stopped updating may still be running, so it is still the
  // user's to stop - and stopping one whose run is gone ends it.
  const canStop = awaitingAgentReply || turnPhase === "stalled";

  const patchedPendingSessionRef = useRef<string | null>(null);
  /** Changes the tray has been read for, so each is read once, not per frame. */
  const seenPendingChangesRef = useRef<Set<string>>(new Set());
  /** The reply whose run was last looked up because the cache did not know it. */
  const runLookupRef = useRef<string | null>(null);
  useEffect(() => {
    patchedPendingSessionRef.current = null;
    seenPendingChangesRef.current = new Set();
    streamedRowsRef.current = new Map();
    setStreamDown(false);
    setStopRequested(false);
    stopRequestedRef.current = false;
    // Files dropped on one thread are not sent with another's next message.
    draftMessageIdentifierRef.current = crypto.randomUUID();
    setDraftFiles([]);
  }, [activeThreadNodeId]);

  const { restart: restartStream } = useSubscription(
    Subscriptions.STREAM_THREAD,
    {
      skip: !streamThreadIdentifier,
      variables: { threadIdentifier: streamThreadIdentifier! },
      onError: () => {
        // graphql-ws has already retried the socket and given up. The turn
        // keeps converging over HTTP meanwhile; the stream is retried below.
        setStreamDown(true);
      },
      onData: ({ data: subData }) => {
        const event = (subData.data as { streamThread?: ThreadStreamEvent })
          ?.streamThread;
        if (!event || !activeThreadNodeId) return;
        setStreamDown(false);
        reconciler.noteFrame();
        const messageIdentifier = event.messageIdentifier;
        const isTurnOver = Boolean(event.stopped || event.done || event.error);

        // A stopped turn stops changing on screen, so nothing the stream says
        // about it lands: not the rows the run keeps writing as it winds down,
        // and not its ending, which carries those same rows in the finished
        // turn. The record keeps all of it and the thread shows it again on
        // the next read; sending the next message picks the stream back up.
        if (stopRequestedRef.current) return;

        // The run behind this frame started after the list was read, so the
        // cache does not know it yet (in prod it starts on a Pub/Sub push
        // seconds after the send). Read the thread once, so the turn's status
        // comes from the server rather than from which frames happen to land.
        if (
          messageIdentifier &&
          agentReply?.identifier === messageIdentifier &&
          agentReply.execution == null &&
          runLookupRef.current !== messageIdentifier
        ) {
          runLookupRef.current = messageIdentifier;
          void refetchThread().catch(() => undefined);
        }

        if (event.content?.identifier && messageIdentifier) {
          writeStreamingContent(
            apolloClient.cache,
            messageIdentifier,
            event.content,
          );
          frameSeqRef.current += 1;
          streamedRowsRef.current.set(event.content.identifier, {
            messageIdentifier,
            content: event.content,
            seq: frameSeqRef.current,
          });

          const sessionId = event.content.pendingSessionId;
          if (sessionId && patchedPendingSessionRef.current !== sessionId) {
            patchedPendingSessionRef.current = sessionId;
            setThreadPendingSession(
              apolloClient.cache,
              activeThreadNodeId,
              sessionId,
            );
          }

          // The tray lists the session's changes, which the stream does not
          // carry: a frame only names the change its row made, and one tool
          // call can make several. Read the session when a change is new, so
          // the tray shows it as the card does rather than when the turn ends.
          // A bulk write makes many changes and names none of them, so the
          // tray is read once when it finishes instead.
          const changeId =
            event.content.pendingChange?.identifier ??
            (/^bulk_(create|update)_/.test(event.content.toolName ?? "") &&
            event.content.status === "done"
              ? event.content.identifier
              : undefined);
          if (changeId && !seenPendingChangesRef.current.has(changeId)) {
            seenPendingChangesRef.current.add(changeId);
            void apolloClient
              .query({
                query: Queries.THREAD_PENDING_SESSION_QUERY,
                variables: { id: activeThreadNodeId },
                fetchPolicy: "network-only",
              })
              .catch(() => undefined);
          }
        }

        if (event.error) {
          setSnackbarMessage(formatApolloError(event.error));
        }

        if (isTurnOver) {
          // The finished turn shares its identifiers with what was streamed, so
          // this updates those cache entries in place rather than swapping rows.
          // It carries the run's final status, which is what ends the turn.
          if (event.message) {
            writePersistedMessage(
              apolloClient.cache,
              activeThreadNodeId,
              event.message as MessageItem,
            );
          }
          // Every subscribe replays the thread's last turn, so a ``done`` also
          // arrives when a panel opens; only a turn we were waiting on has
          // anything new for the list or the thread's name.
          if (awaitingAgentReply) {
            void refetchThread().catch(() => undefined);
            void apolloClient.refetchQueries({ include: ["Threads"] });
          }
        }
      },
    },
  );

  useEffect(() => {
    if (!streamDown) return;
    const timer = window.setInterval(() => restartStream(), 5_000);
    return () => window.clearInterval(timer);
  }, [streamDown, restartStream]);

  const lastUserText = useMemo(() => {
    for (let index = messageNodes.length - 1; index >= 0; index--) {
      const message = messageNodes[index];
      if (message.account == null) continue;
      const text = (message.content ?? [])
        .map((row) => row.text ?? "")
        .join(" ")
        .trim();
      return text || null;
    }
    return null;
  }, [messageNodes]);

  const turnNotice = useMemo((): TurnNotice | null => {
    // Said as what it does: the message goes again, as a new turn.
    const retryTurn = lastUserText
      ? {
          label: "Send again",
          onClick: () => handleSendMessage([{ text: lastUserText }]),
        }
      : undefined;
    switch (turnPhase) {
      case "send-failed":
        return {
          text: "Your message wasn't sent.",
          action: failedSend
            ? { label: "Retry", onClick: () => handleSendMessage(failedSend) }
            : undefined,
          onDismiss: () => {
            setFailedSend(null);
            setPendingPrompt(null);
          },
        };
      // It may still be running - a long tool call, a dropped connection - so
      // it is reached again, not sent again: sent, a second turn ran over the
      // first.
      case "stalled":
        return {
          text: "This reply stopped updating. It may still be running.",
          action: {
            label: "Reconnect",
            onClick: () => {
              restartStream();
              reconciler.retryNow();
            },
          },
        };
      case "failed":
        return {
          text: "The agent couldn't finish this reply.",
          action: retryTurn,
        };
      case "empty":
        return {
          text: "The agent didn't return a response.",
          action: retryTurn,
        };
    }
    if (reconciler.unreachable) {
      return {
        text: "Can't reach the server. Retrying…",
        action: { label: "Retry now", onClick: reconciler.retryNow },
      };
    }
    if (streamDown && awaitingAgentReply) {
      return {
        text: "Live updates are paused. The reply will still arrive.",
        action: { label: "Reconnect", onClick: restartStream },
      };
    }
    return null;
  }, [
    turnPhase,
    failedSend,
    lastUserText,
    handleSendMessage,
    reconciler.unreachable,
    reconciler.retryNow,
    streamDown,
    awaitingAgentReply,
    restartStream,
  ]);

  useEffect(() => {
    if (!threadFragment?.name || !activeThreadNodeId) return;
    setThreadTabLabel(
      threadNodeIdToTabId(activeThreadNodeId),
      threadFragment.name,
    );
  }, [threadFragment?.name, activeThreadNodeId, setThreadTabLabel]);

  useEffect(() => {
    setThreadTabActivity(threadNodeIdToTabId(activeThreadNodeId), {
      isStreaming: awaitingAgentReply,
      pendingDeltaCount: Math.max(
        pendingNodes.length,
        pendingDeltaCountFromSession(threadFragment?.pendingSession),
      ),
    });
  }, [
    activeThreadNodeId,
    awaitingAgentReply,
    pendingNodes.length,
    threadFragment?.pendingSession,
    setThreadTabActivity,
  ]);

  const resetThread = useCallback(() => {
    const {
      activeMessageContext,
      setActiveMessageContext,
      setActiveMessageIndex,
    } = State.getState();
    // A new thread starts with nothing added, but on the same page.
    setActiveMessageContext(activeMessageContext.filter((item) => item.isPage));
    setActiveMessageIndex(0);
    setFailedSend(null);
    setQueuedNewThread(null);
    setPendingPrompt(null);
    setStreamThreadIdentifierOverride(null);
    draftMessageIdentifierRef.current = crypto.randomUUID();
    setDraftFiles([]);
    openNewThreadTab();
  }, [openNewThreadTab]);

  return {
    userEntity,
    newThreadEntityIdentifier,
    setNewThreadEntityIdentifier,
    activeThreadNodeId,
    setActiveThreadNodeId,
    openThreadTab,
    openNewThreadTab,
    turnPhase,
    turnNotice,
    pendingChangesExpanded,
    setPendingChangesExpanded,
    isReviewingChanges,
    pendingNodes,
    pendingSessionId,
    awaitingAgentReply,
    threadFragment,
    messagesForList,
    messageEdges,
    pendingPrompt: shownPendingPrompt,
    pendingContext,
    pendingFiles,
    isLoading,
    threadQueryError,
    refetchThread,
    hasMoreMessages,
    loadMoreMessages,
    isWorkflowExecutionThread,
    isInitialThreadLoading,
    rootRef,
    scrollHostRef,
    loadMoreRef,
    createThread,
    creatingThreadLoading,
    updatingThreadLoading,
    handleStopThread,
    canStop,
    stoppingThread,
    stopRequested,
    handleConfirmPending,
    handleRejectPending,
    confirmingSession,
    rejectingSession,
    handleSendMessage,
    draftFiles,
    handleDropFiles,
    handleRemoveDraftFile,
    resetThread,
  };
}

const TURN_TIMING = {
  /** How long a turn in progress may go without a stream frame before the panel re-reads the thread. */
  quietMs: 3_000,
  /**
   * How long a turn in progress may show no sign of life before it is stalled.
   * A running turn's heartbeat advances ``execution.updatedAt`` every 10s, so
   * this only trips for a turn whose worker is gone.
   */
  stallMs: 45_000,
  /** Longest gap between re-reads while the server cannot be reached. */
  maxRetryMs: 20_000,
};

/**
 * Keeps a turn in progress converging on what the server has saved.
 *
 * The stream is how a reply arrives quickly, not how the panel learns the
 * turn is over. While ``inProgress`` and no frame has arrived for
 * ``quietMs``, the thread is re-read over HTTP - backing off while that
 * fails, which is reported as ``unreachable``. A turn whose ``progressKey``
 * has not moved and that has had no frame for ``stallMs`` is ``stalled``.
 */
export function useAgentTurnReconciler({
  inProgress,
  progressKey,
  refetch,
  timing = TURN_TIMING,
}: {
  inProgress: boolean;
  progressKey: string;
  refetch: () => Promise<unknown>;
  timing?: typeof TURN_TIMING;
}) {
  const [stalled, setStalled] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const lastFrameAtRef = useRef(0);
  const lastProgressAtRef = useRef(0);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    lastProgressAtRef.current = Date.now();
    setStalled(false);
  }, [progressKey]);

  useEffect(() => {
    if (!inProgress) return;
    const now = Date.now();
    lastFrameAtRef.current = now;
    lastProgressAtRef.current = now;
  }, [inProgress]);

  useEffect(() => {
    if (!inProgress) {
      setStalled(false);
      setUnreachable(false);
      return;
    }
    let cancelled = false;
    let failures = 0;
    let timer: number | undefined;
    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - lastProgressAtRef.current >= timing.stallMs) {
        setStalled(true);
      }
      if (Date.now() - lastFrameAtRef.current >= timing.quietMs) {
        try {
          await refetchRef.current();
          failures = 0;
          if (!cancelled) setUnreachable(false);
        } catch {
          failures += 1;
          if (!cancelled) setUnreachable(true);
        }
      }
      if (cancelled) return;
      timer = window.setTimeout(
        tick,
        Math.min(timing.quietMs * 2 ** failures, timing.maxRetryMs),
      );
    };
    timer = window.setTimeout(tick, attempt > 0 ? 0 : timing.quietMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [inProgress, attempt, timing]);

  const noteFrame = useCallback(() => {
    const now = Date.now();
    lastFrameAtRef.current = now;
    lastProgressAtRef.current = now;
    setStalled(false);
  }, []);
  const retryNow = useCallback(() => {
    lastFrameAtRef.current = 0;
    setAttempt((value) => value + 1);
  }, []);

  return { stalled, unreachable, noteFrame, retryNow };
}

export function useThreadTabStatus(tabId: ThreadIdType) {
  const storeActivity = State((s) => s.threadTabActivity[tabId]);
  const threadNodeId = tabIdToThreadNodeId(tabId);
  const canReadCache = threadNodeId != null && tabId !== NEW_THREAD_THREAD_ID;

  const { data } = useFragment<ThreadTabStatusFragment>({
    fragment: THREAD_TAB_STATUS_FRAGMENT,
    fragmentName: "ThreadTabStatus",
    from: {
      __typename: "ThreadObject" as const,
      id: canReadCache ? threadNodeId : "__thread_tab_skip__",
    },
  });

  const pendingFromCache = useMemo(
    () =>
      canReadCache
        ? pendingDeltaCountFromSession(data?.pendingSession ?? null)
        : 0,
    [canReadCache, data?.pendingSession],
  );

  const isStreaming = storeActivity?.isStreaming ?? false;
  const pendingDeltaCount = Math.max(
    storeActivity?.pendingDeltaCount ?? 0,
    pendingFromCache,
  );

  return { isStreaming, pendingDeltaCount };
}

/**
 * Offer *reference* to the chat as the page's own while the chat is open: it
 * shows along the composer's foot rather than among what the user added.
 * Taken back when it changes, when the chat shuts, or when the page goes;
 * ``null`` offers nothing.
 */
export function usePageThreadReference(
  reference: Omit<ThreadMessageContextItemType, "isPage"> | null,
) {
  const isThreadPanelOpen = State((state) => state.isThreadPanelOpen);
  // By what it says rather than which object it is: a page builds it afresh
  // each render.
  const offered = reference ? JSON.stringify(reference) : null;
  useEffect(() => {
    if (!isThreadPanelOpen || !offered) return;
    const item: ThreadMessageContextItemType = {
      ...(JSON.parse(offered) as ThreadMessageContextItemType),
      isPage: true,
    };
    const withoutIt = () =>
      State.getState().activeMessageContext.filter(
        (other) => other.id !== item.id,
      );
    State.getState().setActiveMessageContext([...withoutIt(), item]);
    return () => State.getState().setActiveMessageContext(withoutIt());
  }, [isThreadPanelOpen, offered]);
}
